/**
 * EmailVerify (Sprint 5 B5) — landing page sau khi user click link trong email.
 *
 * URL: /verify-email/<token>
 * Behavior:
 *   - Mount → GET /api/auth/verify-email/<token>/
 *   - 200 → show success + auto-redirect /login sau 3s
 *   - 400 → show error (token hết hạn / đã dùng / invalid)
 */

import { useEffect, useState } from "react";
import { Button, Card, Result, Spin } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { verifyEmail } from "@/api/auth";

type Status = "loading" | "success" | "error";

export function EmailVerify() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<string>("");
  const [email, setEmail] = useState<string | undefined>();

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Token không có trong URL.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await verifyEmail(token);
        if (cancelled) return;
        setStatus("success");
        setMessage(resp.detail);
        setEmail(resp.email);
        // Auto-redirect login sau 3s
        setTimeout(() => {
          if (!cancelled) navigate("/login", { replace: true });
        }, 3000);
      } catch (err: unknown) {
        if (cancelled) return;
        const detail =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail;
        setStatus("error");
        setMessage(detail ?? "Xác thực thất bại. Vui lòng thử lại.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f4f5f7",
      }}
    >
      <Card style={{ width: 480, padding: "16px 8px" }}>
        {status === "loading" && (
          <div style={{ textAlign: "center", padding: 32 }} data-testid="verify-loading">
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#7a8499" }}>
              Đang xác thực email...
            </div>
          </div>
        )}

        {status === "success" && (
          <Result
            status="success"
            title="Xác thực thành công"
            subTitle={
              email
                ? `Tài khoản ${email} đã sẵn sàng. Đang chuyển về trang đăng nhập...`
                : message
            }
            extra={[
              <Link to="/login" key="login">
                <Button type="primary">Đăng nhập ngay</Button>
              </Link>,
            ]}
          />
        )}

        {status === "error" && (
          <Result
            status="error"
            title="Xác thực thất bại"
            subTitle={message}
            extra={[
              <Link to="/signup" key="signup">
                <Button type="primary">Đăng ký lại</Button>
              </Link>,
              <Link to="/login" key="login">
                <Button>Về đăng nhập</Button>
              </Link>,
            ]}
          />
        )}
      </Card>
    </div>
  );
}

export default EmailVerify;
